// Jotly Desktop — Cmd+J floating jot palette. Centred Spotlight-style input.
// Wired to the real capture pipeline (POST /messages, capture-ack).
//
// Capture is OPTIMISTIC and the palette stays open: Enter clears the box
// immediately, shows a quiet "Jotted & safe." ack, and keeps focus so the next
// thought can follow straight away (rapid-fire dumping). If a save fails the
// text is restored to the input — nothing is ever lost. Shift+Enter inserts a
// new line (the input is a real auto-growing textarea), ⌘Enter saves AND
// routes the same text into the Talk chat as a recall question, Esc dismisses.
// Pasting a screenshot captures via POST /photos (OCR).

import { useEffect, useRef, useState } from 'react';
import { Mark } from './atoms';
import { sendMessage, sendPhoto } from '../../api/client';
import { fileToBase64, firstImageFile } from '../../api/capture';

type Ack = { kind: 'saved' | 'busy' | 'error'; message: string } | null;

const MAX_HEIGHT = 200;

export function CmdJOverlay({
  open,
  onClose,
  onAsk,
}: {
  open: boolean;
  onClose: () => void;
  /** Route a question into the Talk centre chat (⌘Enter). */
  onAsk?: (question: string) => void;
}) {
  const [text, setText] = useState('');
  const [ack, setAck] = useState<Ack>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const ackTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    setText('');
    setAck(null);
    // Focus after the entry animation so the caret doesn't appear before the
    // panel does.
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => () => {
    if (ackTimer.current) window.clearTimeout(ackTimer.current);
  }, []);

  const showAck = (next: NonNullable<Ack>, clearAfterMs?: number) => {
    if (ackTimer.current) window.clearTimeout(ackTimer.current);
    setAck(next);
    if (clearAfterMs) {
      ackTimer.current = window.setTimeout(() => setAck(null), clearAfterMs);
    }
  };

  const resetHeight = () => {
    const el = inputRef.current;
    if (el) el.style.height = 'auto';
  };

  const autoGrow = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  };

  // Optimistic capture: clear the input straight away and keep the palette
  // open + focused; the save resolves in the background. On failure, restore
  // the text to the input (ahead of anything typed since) so it isn't lost.
  const save = (alsoAsk: boolean) => {
    const v = text.trim();
    if (!v) return;
    setText('');
    resetHeight();
    showAck({ kind: 'saved', message: 'Jotted & safe.' }, 2000);
    if (alsoAsk) {
      // Attention moves to the Talk thread for the answer.
      onAsk?.(v);
      onClose();
    } else {
      inputRef.current?.focus();
    }
    sendMessage(v).catch(() => {
      setText((current) => (current ? `${v}\n${current}` : v));
      requestAnimationFrame(autoGrow);
      showAck({ kind: 'error', message: "Couldn't save — it's back in the box, try again." });
    });
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const file = firstImageFile(e.clipboardData);
    if (!file) return; // a pasted link stays as text — the pipeline unfurls it
    e.preventDefault();
    showAck({ kind: 'busy', message: 'Reading the image…' });
    try {
      const b64 = await fileToBase64(file);
      await sendPhoto(b64);
      showAck({ kind: 'saved', message: 'Jotted & safe.' }, 2000);
      inputRef.current?.focus();
    } catch {
      showAck({ kind: 'error', message: "Couldn't read that image — try again." });
    }
  };

  if (!open) return null;

  return (
    <div className="jd-cmdj-overlay" onClick={onClose}>
      <div className="jd-cmdj" onClick={(e) => e.stopPropagation()}>
        <div className="jd-cmdj-input">
          <Mark size={22} />
          <textarea
            ref={inputRef}
            value={text}
            rows={1}
            onChange={(e) => {
              setText(e.target.value);
              autoGrow();
            }}
            onPaste={handlePaste}
            placeholder="Tell Jotly anything…"
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              else if (e.key === 'Enter' && !e.shiftKey) {
                // Shift+Enter falls through to the textarea's own newline.
                e.preventDefault();
                save(e.metaKey || e.ctrlKey);
              }
            }}
          />
        </div>
        <div className="jd-cmdj-footer">
          {ack ? (
            <span
              style={
                ack.kind === 'saved'
                  ? { color: 'var(--accent)' }
                  : ack.kind === 'error'
                    ? { color: 'var(--accent-strong)' }
                    : undefined
              }
            >
              {ack.message}
            </span>
          ) : (
            <>
              <span>
                <span className="kbd">↵</span> jot
              </span>
              <span>
                <span className="kbd">⌘↵</span> jot &amp; ask
              </span>
              <span>
                <span className="kbd">⇧↵</span> new line
              </span>
            </>
          )}
          <span style={{ marginLeft: 'auto' }}>
            <span className="kbd">esc</span> dismiss
          </span>
        </div>
      </div>
    </div>
  );
}
